import { Citation } from '../types/citation';

/**
 * Structured LLM Response Format
 */
export interface StructuredLLMResponse {
  Text?: string; // Optional - may not always be provided by LLM
  products: ProductReference[];
}

export interface ProductReference {
  product_url: string;
  price?: string;
  color?: string;
  sizes?: string[];
  product_desc?: string;
  item_type?: string;
  style?: string[];
  silhouette?: string;
  color_palette?: string[];
  gender?: string;
  season?: string;
}

/**
 * Parse structured JSON response from LLM
 * Expected formats:
 * 1. { "Text": "Response text", "products": [...] }
 * 2. { "Text": "Response text" }  // Text only, no products
 * 3. { "products": [...] }  // Products only, no Text
 */
export function parseStructuredResponse(content: string): StructuredLLMResponse | null {
  try {
    // Direct JSON parsing - cleaner and more reliable than regex
    const trimmedContent = content.trim();
    
    // Check if content looks like JSON (starts with { and ends with })
    if (!trimmedContent.startsWith('{') || !trimmedContent.endsWith('}')) {
      return null;
    }

    const parsed = JSON.parse(trimmedContent) as StructuredLLMResponse;
    
    // Validate structure - must have either Text or products (or both)
    const hasText = typeof parsed.Text === 'string';
    const hasProducts = Array.isArray(parsed.products);
    
    if (hasText || hasProducts) {
      // Ensure both fields exist even if empty for consistency
      if (!parsed.Text) {
        parsed.Text = '';
      }
      if (!parsed.products) {
        parsed.products = [];
      }
      return parsed;
    }
    
    return null;
  } catch (error) {
    // Not valid JSON or doesn't match our structure - treat as plain text
    return null;
  }
}

/**
 * Extract product URLs from structured response
 */
export function extractProductUrlsFromStructured(content: string): string[] {
  const structured = parseStructuredResponse(content);
  
  if (!structured || !structured.products) {
    return [];
  }
  
  return structured.products
    .map(product => product.product_url)
    .filter(url => url && url.length > 0);
}

/**
 * Convert structured response products to Citation format for compatibility
 */
export function convertProductsToCitations(content: string): Citation[] {
  const structured = parseStructuredResponse(content);
  
  if (!structured || !structured.products) {
    return [];
  }
  
  return structured.products.map((product, index) => ({
    url: product.product_url,
    title: `Product ${index + 1}${product.color ? ` - ${product.color}` : ''}`,
    content: [
      product.price && `Price: ${product.price}`,
      product.color && `Color: ${product.color}`,
      product.sizes && product.sizes.length > 0 && `Sizes: ${product.sizes.join(', ')}`
    ].filter(Boolean).join(' | ') || product.product_url
  }));
}

/**
 * Extract clean text from structured response (without JSON)
 */
export function extractTextFromStructured(content: string): string {
  const structured = parseStructuredResponse(content);
  
  if (structured) {
    // Return the Text field if it exists, otherwise empty string
    // Don't fall back to full content as that would show the entire JSON
    return structured.Text !== undefined ? structured.Text : '';
  }
  
  // Fallback: return original content if not structured
  return content;
}

/**
 * Check if content contains a structured response
 */
export function isStructuredResponse(content: string): boolean {
  return parseStructuredResponse(content) !== null;
}

/**
 * Extract complete products from streaming partial JSON.
 * Works incrementally as JSON is being streamed, extracting only fully-formed product objects.
 * 
 * This enables "Streaming Product Reveal" - products appear in the UI as the LLM mentions them,
 * rather than waiting for the complete JSON response.
 * 
 * Example partial JSON:
 * {"Text": "Hello", "products": [{"product_url": "url1", "price": "$10"},{"product_url": "url2"
 *                                 ↑ complete (returned)                  ↑ incomplete (skipped)
 * 
 * @param content - Partial or complete JSON string being streamed
 * @returns Array of ProductReference objects that are fully formed
 */
export function extractProductsFromPartialJSON(content: string): ProductReference[] {
  if (!content || !content.includes('"products"')) {
    return [];
  }
  
  const products: ProductReference[] = [];
  
  // Find the start of the products array
  const productsMatch = content.match(/"products"\s*:\s*\[/);
  if (!productsMatch || productsMatch.index === undefined) {
    return [];
  }
  
  const productsStartIndex = productsMatch.index + productsMatch[0].length;
  const productsContent = content.substring(productsStartIndex);
  
  // Find complete product objects using bracket matching
  // This handles nested objects, arrays, and escaped strings correctly
  let depth = 0;
  let objectStart = -1;
  let inString = false;
  let escapeNext = false;
  
  for (let i = 0; i < productsContent.length; i++) {
    const char = productsContent[i];
    
    // Handle escape sequences in strings
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    
    if (char === '\\' && inString) {
      escapeNext = true;
      continue;
    }
    
    // Handle string boundaries
    if (char === '"' && !escapeNext) {
      inString = !inString;
      continue;
    }
    
    // Skip if inside a string (don't count brackets in string values)
    if (inString) continue;
    
    // Track object depth
    if (char === '{') {
      if (depth === 0) {
        objectStart = i;
      }
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0 && objectStart !== -1) {
        // Found a complete product object
        const objectStr = productsContent.substring(objectStart, i + 1);
        try {
          const product = JSON.parse(objectStr) as ProductReference;
          // Only include if it has the required product_url field
          if (product.product_url && product.product_url.length > 0) {
            products.push(product);
          }
        } catch {
          // Incomplete or malformed object, skip it
          console.debug('[extractProductsFromPartialJSON] Skipping malformed object:', objectStr.substring(0, 50));
        }
        objectStart = -1;
      }
    } else if (char === ']' && depth === 0) {
      // End of products array reached
      break;
    }
  }
  
  return products;
}

/**
 * Enrich images with product data extracted from streaming partial JSON.
 * Similar to enrichImagesWithProductData but works with ProductReference[] directly.
 * 
 * @param images - Array of images to enrich
 * @param streamingProducts - Products extracted from partial JSON via extractProductsFromPartialJSON
 * @returns Enriched images with product metadata
 */
export function enrichImagesFromStreamingProducts<T extends { 
  product_url: string; 
  price?: string; 
  color?: string; 
  sizes?: string[];
  product_desc?: string;
  item_type?: string;
  style?: string[];
  silhouette?: string;
  color_palette?: string[];
  gender?: string;
  season?: string;
}>(
  images: T[],
  streamingProducts: ProductReference[]
): T[] {
  if (!streamingProducts || streamingProducts.length === 0) {
    return images;
  }
  
  // Create a map of product_url -> product data for fast lookup
  const productDataMap = new Map<string, ProductReference>();
  streamingProducts.forEach(product => {
    const cleanedUrl = product.product_url.trim().toLowerCase();
    productDataMap.set(cleanedUrl, product);
  });
  
  // Enrich images with matching product data
  return images.map(image => {
    const cleanedImageUrl = (image.product_url || '').trim().toLowerCase();
    const productData = productDataMap.get(cleanedImageUrl);
    
    if (productData) {
      return {
        ...image,
        price: productData.price || image.price,
        color: productData.color || image.color,
        sizes: productData.sizes || image.sizes,
        product_desc: productData.product_desc || image.product_desc,
        item_type: productData.item_type || image.item_type,
        style: productData.style || image.style,
        silhouette: productData.silhouette || image.silhouette,
        color_palette: productData.color_palette || image.color_palette,
        gender: productData.gender || image.gender,
        season: productData.season || image.season,
      };
    }
    
    return image;
  });
}

/**
 * Enrich image metadata with product data from structured response
 * Matches images by product_url and adds all product metadata from LLM response
 */
export function enrichImagesWithProductData<T extends { 
  product_url: string; 
  price?: string; 
  color?: string; 
  sizes?: string[];
  product_desc?: string;
  item_type?: string;
  style?: string[];
  silhouette?: string;
  color_palette?: string[];
  gender?: string;
  season?: string;
}>(
  images: T[],
  messageContent: string
): T[] {
  const structured = parseStructuredResponse(messageContent);
  
  if (!structured || !structured.products || structured.products.length === 0) {
    return images;
  }
  
  // Create a map of product_url -> product data for fast lookup
  const productDataMap = new Map<string, ProductReference>();
  structured.products.forEach(product => {
    const cleanedUrl = product.product_url.trim().toLowerCase();
    productDataMap.set(cleanedUrl, product);
  });
  
  // Enrich images with matching product data
  return images.map(image => {
    const cleanedImageUrl = (image.product_url || '').trim().toLowerCase();
    const productData = productDataMap.get(cleanedImageUrl);
    
    if (productData) {
      return {
        ...image,
        price: productData.price || image.price,
        color: productData.color || image.color,
        sizes: productData.sizes || image.sizes,
        product_desc: productData.product_desc || image.product_desc,
        item_type: productData.item_type || image.item_type,
        style: productData.style || image.style,
        silhouette: productData.silhouette || image.silhouette,
        color_palette: productData.color_palette || image.color_palette,
        gender: productData.gender || image.gender,
        season: productData.season || image.season,
      };
    }
    
    return image;
  });
}
